import ModalFooter from "@/modules/common/components/ui-kit/modals/modal-footer";
import ModalHeader from "@/modules/common/components/ui-kit/modals/modal-header";
import { Box } from "@mui/material";
import { Sheet, SheetRef } from "react-modal-sheet";
import { useEffect, useRef, useState } from "react";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";

export interface MobileModalProps {
  open: boolean;
  onClose: (...args: any[]) => any;
  children: React.ReactNode;
  title?: string;
  headerAction?: React.ReactNode;
  cancelButtonText?: string;
  confirmButtonText?: string;
  extraButtonText?: string;
  snapPoints?: number[];
  initialSnap?: number;
  onConfirm?: (...args: any[]) => any;
  onCancel?: (...args: any[]) => any;
  onExtra?: (...args: any[]) => any;
  isLoading?: boolean;
  isConfirmDisabled?: boolean;
  zIndex?: number;
}

const MobileModal = ({
  open,
  onClose,
  children,
  title,
  headerAction,
  cancelButtonText,
  confirmButtonText,
  extraButtonText,
  snapPoints,
  initialSnap = 0,
  onConfirm,
  onCancel,
  onExtra,
  isLoading,
  isConfirmDisabled,
  zIndex,
}: MobileModalProps) => {
  const sheetRef = useRef<SheetRef>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Detect keyboard using VirtualKeyboard API or fallback
  useEffect(() => {
    const isVKSupported =
      typeof navigator !== "undefined" && "virtualKeyboard" in navigator;

    if (isVKSupported) {
      const vk = navigator.virtualKeyboard as any;
      vk.overlaysContent = true;

      const handleGeometryChange = () => {
        const height = vk.boundingRect?.height || 0;
        setKeyboardHeight(height);
      };

      vk.addEventListener("geometrychange", handleGeometryChange);
      return () => {
        vk.removeEventListener("geometrychange", handleGeometryChange);
      };
    } else {
      // Fallback: listen for resize to guess keyboard height
      const initialHeight = window.innerHeight;

      const handleResize = () => {
        const newHeight = window.innerHeight;
        const diff = initialHeight - newHeight;
        setKeyboardHeight(diff > 150 ? diff : 0);
      };

      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  return (
    <Sheet
      ref={sheetRef}
      isOpen={open}
      onClose={onClose}
      snapPoints={snapPoints}
      initialSnap={initialSnap}
      style={{ zIndex: zIndex || nawyTheme.zIndex.modal }}
    >
      <Sheet.Container
        style={{
          backgroundColor: nawyTheme.nawy.colors.semantic.layer.primary,
          borderTopLeftRadius: nawyTheme.nawy.radius["2xl"],
          borderTopRightRadius: nawyTheme.nawy.radius["2xl"],
          paddingBottom: keyboardHeight,
          ...(snapPoints ? {} : { height: "auto", maxHeight: "90vh" }),
        }}
      >
        <Sheet.Header
          style={{
            backgroundColor: nawyTheme.nawy.colors.semantic.layer.primary,
            borderTopLeftRadius: nawyTheme.nawy.radius["2xl"],
            borderTopRightRadius: nawyTheme.nawy.radius["2xl"],
          }}
        />

        <Sheet.Content
          style={{
            display: "flex",
            flexDirection: "column",
            ...(snapPoints ? { height: "100%" } : { height: "auto" }),
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              ...(snapPoints
                ? { height: "100%", overflow: "hidden" }
                : { height: "auto" }),
              minHeight: 0,
            }}
          >
            {title && (
              <ModalHeader
                title={title}
                onClose={onClose}
                action={headerAction}
              />
            )}

            <Box
              sx={{
                ...(snapPoints
                  ? { flex: "1 1 auto", overflow: "auto" }
                  : { height: "auto" }),
                overflowY: "auto",
                padding: nawyTheme.nawy.spacing.md,
              }}
            >
              {children}
            </Box>

            {(cancelButtonText || confirmButtonText) && (
              <ModalFooter
                cancelButtonText={cancelButtonText}
                confirmButtonText={confirmButtonText}
                extraButtonText={extraButtonText}
                onCancel={onCancel ?? onClose}
                onConfirm={onConfirm}
                onExtra={onExtra}
                isLoading={isLoading}
                isConfirmDisabled={isConfirmDisabled}
              />
            )}
          </Box>
        </Sheet.Content>
      </Sheet.Container>
      <Sheet.Backdrop onTap={onClose} />
    </Sheet>
  );
};

export default MobileModal;
